import React, { useEffect, useContext, useState } from 'react';
import { Form, Input, DatePicker, Row, Col, Select, Checkbox } from 'antd';
import { UserContext } from '../../../contexts/UserContext';
import dayjs from 'dayjs';
import { getEquipments, getInspection } from '../../../utils/api';

const { Option } = Select;

const InspectionForm = ({ form ,incomingData}) => {
  const { user } = useContext(UserContext);
  const [equipmentList, setEquipmentList] = useState([]);
  const [inspectionData, setInspectionData] = useState([]);
  const [reportNo, setReportNo] = useState('');

  // Watch checkbox and form fields
  const isNewEntry = Form.useWatch('isNewEntry', form) || false; // Default to false if undefined
  const equipmentId = Form.useWatch('equipment', form);
  const equipmentNumber = Form.useWatch('equipment_number', form);
  const diameter = Form.useWatch('diameter', form);

  // Generate equipment_no when fields change in new entry mode
  useEffect(() => {
    if (isNewEntry && equipmentId && equipmentNumber && diameter) {
      const selected = equipmentList.find((eq) => eq.id === equipmentId);
      const name = selected?.name?.replace(/\s+/g, '-').toUpperCase() || 'EQ';
      const code = `${name}-${equipmentNumber}-${diameter}`;
      form.setFieldsValue({ equipment_no: code });
    }
  }, [equipmentId, equipmentNumber, diameter, isNewEntry, equipmentList, form]);




  // Reset form and set defaults when toggling isNewEntry
  useEffect(() => {
    form.resetFields(); // Reset all fields
    const autoReportNo = `RPT-${dayjs().format('YYYYMMDD-HHmmss')}`;
    setReportNo(autoReportNo);

    if (isNewEntry) {
      // New entry mode: reset to defaults
      form.setFieldsValue({
        isNewEntry: true,
        customer_name: '',
        plant_location: '',
        equipment: '',
        diameter: 0,
        length_: 0,
        equipment_number: '',
        equipment_no: '',
        inspection_date: dayjs(), // Store as string
        inspected_by: user?.name || '',
        report_no: autoReportNo,
        liner_type: '',
        data_by_column:'',
          notes : ''
      });
    } else {
      // Existing entry mode: clear fields except isNewEntry and report_no
      form.setFieldsValue({
        isNewEntry: false,
        customer_name: '',
        plant_location: '',
        equipment: undefined,
        diameter: undefined,
        length_: undefined,
        equipment_number: undefined,
        equipment_no: '',
        inspection_date: null,
        inspected_by: user?.name || '',
        report_no: autoReportNo,
        liner_type: '',
        unq_equipment_no: undefined,
        data_by_column : '',
        notes : ''
      });
    }
  }, [isNewEntry, form, user]);

  // Auto-fill fields from existing inspection
  const handleEquipmentNoSelect = (selectedId) => {
    const selected = inspectionData.find((eq) => eq.id === selectedId);
    if (!selected) return;

    const name = selected.equipment_no || 'EQ';
    const number = selected.equipment_number || '00';
    const dia = selected.diameter || '0';
    const code = selected.equipment_no;

    form.setFieldsValue({
      customer_name: selected.customer_name || '',
      plant_location: selected.plant_location || '',
      equipment: selected.equipment_id || selected.equipment?.id, // Adjust based on API response
      diameter: dia,
      length_: selected.length_ || '',
      equipment_number: number,
      equipment_no: code,
      liner_type: selected.liner_type || '',
      inspection_date: dayjs(), // selected.inspection_date ? dayjs(selected.inspection_date) : null,

      inspected_by: user?.name || selected.inspected_by || '',
      report_no: selected.report_no || reportNo,
      unq_equipment_no: selectedId,

      ore: selected?.ore || '',
      feed_size: selected.feed_size || '',
      product_size: selected?.product_size ,
       top_ball_size: selected.top_ball_size || '',
      rpm: selected?.rpm,
      data_by_column : selected.data_by_column,
            notes : selected.notes

    });
  };

  // Fetch equipment and inspection data
  useEffect(() => {
    const fetchEquipments = async () => {
      try {
        const result = await getEquipments();
        setEquipmentList(result.data);
        localStorage.setItem('equipments',JSON.stringify(result.data))
      } catch (error) {
        console.error('Error fetching equipment list:', error);
      }
    };

    const fetchInspectionData = async () => {
      try {
        const result = await getInspection();
        setInspectionData(result.data);
      } catch (error) {
        console.error('Error fetching inspection list:', error);
      }
    };

    fetchEquipments();
    fetchInspectionData();
  }, []);

useEffect(() => {
  if (incomingData?.id) {
    form.setFieldsValue({ unq_equipment_no: incomingData.id });
    handleEquipmentNoSelect(incomingData.id); // ✅ This fills the rest
  }
}, [incomingData, inspectionData]); 

  return (
    <>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="isNewEntry" valuePropName="checked">
            <Checkbox>New Inspection</Checkbox>
          </Form.Item>
        </Col>
        {!isNewEntry && (
          <Col span={12}>
            <Form.Item
              label="Search by Unique Equipment No."
              name="unq_equipment_no"
              rules={[{ required: !isNewEntry, message: 'Please select an equipment' }]}
            >
              <Select
                showSearch
                placeholder="Search equipment"
                optionFilterProp="children"
                onChange={handleEquipmentNoSelect}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {inspectionData.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.equipment_summary}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Customer Name"
            name="customer_name"
            rules={[{ required: true, message: 'Please enter customer name' }]}
          >
            <Input placeholder="Enter customer name" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Plant Location"
            name="plant_location"
            rules={[{ required: true, message: 'Please enter plant location' }]}
          >
            <Input placeholder="Enter plant location" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Equipment"
            name="equipment"
            rules={[{ required: true, message: 'Please select equipment' }]}
          >
            <Select placeholder="Select equipment" disabled={!isNewEntry}>
              {equipmentList.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Diameter[mm]"
            name="diameter"
            rules={[{ required: isNewEntry, message: 'Please enter diameter' }]}
          >
            <Input placeholder="e.g. 15" type="number" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Length[mm]" name="length_">
            <Input placeholder="e.g. 15" type="number" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Equipment Number"
            name="equipment_number"
            rules={[{ required: isNewEntry, message: 'Please enter equipment number' }]}
          >
            <Input placeholder="e.g. 01" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Unique Equipment No."
            name="equipment_no"
            rules={[{ required: true, message: 'Equipment number is required' }]}
          >
            <Input placeholder="Auto-generated code" readOnly />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Inspection Date"
            name="inspection_date"
            rules={[
              { required: true, message: 'Please select inspection date' },
              
            ]}
          >
           <DatePicker
              format="DD/MM/YYYY"
             // showTime={{ format: 'HH:mm:ss' }}
              style={{ width: '100%' }}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Inspected By"
            name="inspected_by"
            rules={[{ required: true, message: 'Please enter inspected by' }]}
          >
            <Input placeholder={user?.name || ''} readOnly />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Report No."
            name="report_no"
            rules={[{ required: true, message: 'Report number is required' }]}
          >
            <Input placeholder="Auto-generated report number" readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Liner Supplier" name="liner_type">
            <Input placeholder="Optional" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={4}>
          <Form.Item
            label="Ore"
            name="ore"
            rules={[{ required: false, message: 'Ore number is not required' }]}
          >
            <Input placeholder="Optional" disabled={!isNewEntry}  />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Feed Size[F80]" name="feed_size">
            <Input placeholder="Optional" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
         <Col span={4}>
          <Form.Item label="Product Size[P80]" name="product_size">
            <Input placeholder="Optional" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
         <Col span={4}>
          <Form.Item label="Top Ball Size[mm]" name="top_ball_size">
            <Input placeholder="Optional" disabled={!isNewEntry} />
          </Form.Item>
        </Col>
          <Col span={4}>
          <Form.Item label="RPM" name="rpm">
            <Input placeholder="Optional" disabled={!isNewEntry} />
          </Form.Item>
        </Col>

        <Form.Item name="data_by_column" hidden>
            <Input />
        </Form.Item>

        <Form.Item name="notes" hidden>
            <Input />
        </Form.Item>
      </Row>
    </>
  );
};

export default InspectionForm;